/**
 * Which sources the picker offers, and the small amount of per-source
 * knowledge the shell needs (MK-4a).
 *
 * Extracted so the session module holds a dialog rather than a registry. The
 * shapes here are exactly what `editing/image-picker.js` carried before the
 * rebuild; nothing about adapter selection changed.
 */

import { stockPhotos } from '../adapters/stock-aggregator.js';
import { localImages, getCategories } from '../adapters/local-images.js';
import { logoNuriKits } from '../adapters/logonuri.js';
import { siteImages, siteImageCount } from '../adapters/site-images.js';
import { logonuriAvailability } from '../../templates/logonuri-api.js';

export const UPLOAD_TAB_KEY = 'upload';
export const LAST_TAB_STORAGE_KEY = 'pb-image-picker-last-tab';
export const LAST_QUERY_STORAGE_KEY = 'pb-image-picker-last-query';
/**
 * Per ADAPTER, never one shared key: a LogoNuri kit slug remembered globally
 * would be carried into the Local tab and silently filter it to nothing.
 */
const LAST_CATEGORY_STORAGE_KEY = 'pb-image-picker-last-category';
export const categoryKeyFor = (tabKey) =>
  tabKey === 'local' ? LAST_CATEGORY_STORAGE_KEY : `${LAST_CATEGORY_STORAGE_KEY}-${tabKey}`;

export const SOURCES = [
  // SU-7. First, because on a project skinned from a URL it is the source most
  // likely to hold the right picture: the client's own photographs. It is the
  // other half of the conservative swap policy — the skinner fills the slots it
  // is confident about, and this is where the rest get filled by a person.
  { key: 'site', adapter: siteImages },
  { key: 'local', adapter: localImages },
  { key: 'logonuri', adapter: logoNuriKits },
  // MK-5: ONE stock source over three providers. The three adapters still
  // exist and are still used directly (the SVG library browses
  // `pixabayVectors`); they are simply no longer three things the user picks
  // between. `editing/adapters/stock-aggregator.js` owns the fan-out.
  { key: 'stock', adapter: stockPhotos }
];

/**
 * Sources that were their own tab before MK-5 and are now inside `stock`.
 * A remembered one has to resolve to something, or the picker opens on a
 * source that no longer exists.
 */
export const RETIRED_STOCK_KEYS = ['pexels', 'unsplash', 'pixabay'];

/**
 * Same latch the MCP keys section uses: a deployment with the integration dark
 * never renders the source at all, rather than offering one that answers 503
 * the moment it is clicked.
 *
 * SU-7 adds a second latch of the same shape and for the same reason. `site` is
 * per-PROJECT rather than per-deployment: a project that was never skinned from
 * a URL has no inventory, and offering an empty tab is the "affordance offered
 * where it cannot act" failure the decorative-SVG work recorded. The count is
 * asked of the adapter, which the session fills from the composition context —
 * `sources.js` stays free of any store import, as every adapter here is.
 */
export function visibleSources() {
  return SOURCES.filter(({ key }) => {
    if (key === 'logonuri') return logonuriAvailability() !== false;
    if (key === 'site') return siteImageCount() > 0;
    return true;
  });
}

export function sourceFor(key) {
  return SOURCES.find((entry) => entry.key === key) || null;
}

/**
 * The three KINDS of place an image comes from (MK-4b).
 *
 * Plan decision D1 merges the stock providers into one scope. It does not
 * cover the brand kit, which arrived from a parallel initiative after the plan
 * was written — and a brand-kit importer is a third KIND of source, not a
 * fourth stock provider: it is authenticated, per-account, and vends a curated
 * kit rather than a searchable corpus. It gets its own scope, and removes
 * itself entirely on a deployment where the integration is dark.
 */
export const SCOPES = [
  // SU-7. A fourth KIND, by the same reasoning the brand kit got a third: these
  // are not a searchable corpus and not a curated kit, they are THIS project's
  // own source site, and they exist only for a project skinned from a URL. It
  // leads the control because on such a project it is the likeliest answer, and
  // it disappears entirely on every other project.
  { key: 'site', label: 'This site', sources: ['site'] },
  { key: 'library', label: 'Library', sources: ['local'] },
  { key: 'brand', label: 'Brand', sources: ['logonuri'] },
  { key: 'stock', label: 'Stock', sources: ['stock'] }
];

/**
 * Scopes with at least one visible source, in SCOPES order.
 *
 * Split into a PURE half so the absence case is testable. The Brand segment
 * disappears when `logonuriAvailability()` latches false, and that latch is set
 * only by a real `logonuri_disabled` response — there is no setter, and adding
 * one to another initiative's live module to make this assertable would be a
 * worse trade than making this function take its input.
 */
export function scopesFrom(visibleSourceKeys) {
  const live = new Set(visibleSourceKeys || []);
  return SCOPES.filter((scope) => scope.sources.some((key) => live.has(key))).map((scope) => ({
    ...scope,
    sources: scope.sources.filter((key) => live.has(key))
  }));
}

export function visibleScopes() {
  return scopesFrom(visibleSources().map(({ key }) => key));
}

export function scopeOfSource(sourceKey) {
  return SCOPES.find((scope) => scope.sources.includes(sourceKey))?.key || '';
}

/** The visible sources in a scope, in SCOPES order. Empty for an unknown key. */
export function sourcesInScope(scopeKey) {
  return visibleScopes().find((scope) => scope.key === scopeKey)?.sources || [];
}

const ALL_KEYS = new Set([UPLOAD_TAB_KEY, ...SOURCES.map(({ key }) => key)]);

/**
 * MK-5 migration. An account that last used `pexels` must land on `stock`,
 * not fall through to the default — the stored value is a real preference and
 * `stock` is where that preference now lives. Done at READ time rather than
 * with a one-shot rewrite: a rewrite has to run before anything reads the key,
 * on every entry point, and a missed one is silent.
 */
export function normalizeTabKey(key) {
  const value = String(key || '').trim();
  if (RETIRED_STOCK_KEYS.includes(value)) return 'stock';
  return ALL_KEYS.has(value) ? value : UPLOAD_TAB_KEY;
}

/**
 * The Local source's categories are a module function rather than an adapter
 * method, and predate the hook. Bridging here keeps that call site unchanged
 * while every other adapter answers for itself.
 */
export function hasCategories(entry) {
  return entry?.key === 'local' || typeof entry?.adapter?.getCategories === 'function';
}

export function categoriesFor(entry) {
  if (entry?.key === 'local') return getCategories();
  if (typeof entry?.adapter?.getCategories === 'function') return entry.adapter.getCategories();
  return null;
}

/** `[{value, label}]` from either shape an adapter may answer with. */
export function normalizeCategories(cats) {
  const out = [];
  for (const cat of cats || []) {
    const value = typeof cat === 'string' ? cat : cat?.value;
    if (!value) continue;
    const label =
      typeof cat === 'string' ? cat.charAt(0).toUpperCase() + cat.slice(1) : cat.label || value;
    out.push({ value: String(value), label: String(label) });
  }
  return out;
}
